import { useEffect, useRef, useState, useCallback } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { markAgentLogout } from "../hooks/useTimeTracking";

/**
 * IdleSignout
 *
 * FEATURES
 * - 20 min inactivity logout
 * - 5 min warning modal with live countdown
 * - "Stay Logged In" fully resets idle cycle
 * - Session expired toast
 * - Cross-tab sync (BroadcastChannel + localStorage)
 * - Exempt background admin jobs
 * - Pause idle detection during video / live sessions
 */

const IDLE_CHANNEL = "scope180-auth";
const IDLE_EVENT_KEY = "scope180:logout";
const ACTIVITY_KEY = "scope180:lastActivity";
const WARNING_OFFSET_MS = 5 * 60 * 1000; // 5 minutes before logout
const CHECK_INTERVAL_MS = 10 * 1000; // Check every 10 seconds

export default function IdleSignout({
  auth,
  maxIdleMs = 20 * 60 * 1000, // 20 minutes
  onSignoutPath = "/",
  isAdminBackgroundTaskRunning = false,
  isLiveSessionActive = false,
  children,
}) {
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const userRef = useRef(null);
  const channelRef = useRef(null);

  const [showWarning, setShowWarning] = useState(false);
  const [showExpiredToast, setShowExpiredToast] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  /* -------------------- ACTIVITY TRACKING -------------------- */
  const markActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    // Sync activity across tabs
    localStorage.setItem(ACTIVITY_KEY, String(now));

    // If warning is showing, dismiss it and reset
    if (showWarning) {
      setShowWarning(false);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
  }, [showWarning]);

  /* -------------------- COUNTDOWN TIMER -------------------- */
  const startCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // Calculate initial countdown
    const remaining = maxIdleMs - (Date.now() - lastActivityRef.current);
    setCountdownSeconds(Math.max(0, Math.ceil(remaining / 1000)));

    countdownRef.current = setInterval(() => {
      const remaining = maxIdleMs - (Date.now() - lastActivityRef.current);
      const seconds = Math.max(0, Math.ceil(remaining / 1000));
      setCountdownSeconds(seconds);

      // If countdown reaches 0, force logout
      if (seconds <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
  }, [maxIdleMs]);

  /* -------------------- LOGOUT -------------------- */
  const forceLogout = useCallback(async (reason = "idle") => {
    // Clear intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    try {
      // Mark agent logout for grace period (before signOut invalidates auth)
      await markAgentLogout(auth?.currentUser?.uid);
      await signOut(auth);
    } catch (e) {
      console.error("Signout error:", e);
    } finally {
      const payload = { reason, ts: Date.now() };

      // Notify other tabs
      try {
        channelRef.current?.postMessage(payload);
      } catch (e) {
        // BroadcastChannel might be closed
      }
      localStorage.setItem(IDLE_EVENT_KEY, JSON.stringify(payload));

      setShowExpiredToast(true);
      setShowWarning(false);

      setTimeout(() => {
        window.location.assign(onSignoutPath);
      }, 1500);
    }
  }, [auth, onSignoutPath]);

  /* -------------------- IDLE CHECKER -------------------- */
  const checkIdle = useCallback(() => {
    if (!userRef.current) return;

    // Skip idle check during exempt activities
    if (isAdminBackgroundTaskRunning || isLiveSessionActive) {
      lastActivityRef.current = Date.now();
      return;
    }

    // Check for activity in other tabs
    const storedActivity = localStorage.getItem(ACTIVITY_KEY);
    if (storedActivity) {
      const storedTime = parseInt(storedActivity, 10);
      if (storedTime > lastActivityRef.current) {
        lastActivityRef.current = storedTime;
      }
    }

    const idleTime = Date.now() - lastActivityRef.current;

    // Show warning when approaching timeout
    if (idleTime >= maxIdleMs - WARNING_OFFSET_MS && idleTime < maxIdleMs) {
      if (!showWarning) {
        setShowWarning(true);
        startCountdown();
      }
    }

    // Force logout when idle time exceeded
    if (idleTime >= maxIdleMs) {
      forceLogout("idle");
    }
  }, [maxIdleMs, showWarning, isAdminBackgroundTaskRunning, isLiveSessionActive, forceLogout, startCountdown]);

  /* -------------------- MAIN EFFECT -------------------- */
  useEffect(() => {
    if (!auth || typeof window === "undefined") return;

    // Activity events to track
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "wheel",
    ];

    // Throttle activity marking to avoid excessive updates
    let throttleTimeout = null;
    const throttledMarkActivity = () => {
      if (throttleTimeout) return;
      markActivity();
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
      }, 1000); // Only update once per second max
    };

    // Add event listeners
    events.forEach((e) =>
      window.addEventListener(e, throttledMarkActivity, { passive: true })
    );

    // Set up cross-tab communication
    try {
      channelRef.current = "BroadcastChannel" in window
        ? new BroadcastChannel(IDLE_CHANNEL)
        : null;
    } catch (e) {
      channelRef.current = null;
    }

    const handleRemoteLogout = (data) => {
      if (data?.reason === "idle") {
        setShowExpiredToast(true);
        setShowWarning(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
        setTimeout(() => {
          window.location.assign(onSignoutPath);
        }, 1500);
      }
    };

    if (channelRef.current) {
      channelRef.current.onmessage = (e) => handleRemoteLogout(e.data);
    }

    // Storage event for cross-tab sync (fallback for browsers without BroadcastChannel)
    const onStorage = (e) => {
      if (e.key === IDLE_EVENT_KEY && e.newValue) {
        try {
          handleRemoteLogout(JSON.parse(e.newValue));
        } catch (err) {
          // Invalid JSON
        }
      }
      // Sync activity from other tabs
      if (e.key === ACTIVITY_KEY && e.newValue) {
        const otherTabActivity = parseInt(e.newValue, 10);
        if (otherTabActivity > lastActivityRef.current) {
          lastActivityRef.current = otherTabActivity;
          if (showWarning) {
            setShowWarning(false);
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
          }
        }
      }
    };
    window.addEventListener("storage", onStorage);

    // Auth state listener
    const unsub = onAuthStateChanged(auth, (user) => {
      userRef.current = user;

      if (user) {
        // User logged in - start tracking
        lastActivityRef.current = Date.now();
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        setShowWarning(false);

        // Clear any existing interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Start idle checker
        intervalRef.current = setInterval(checkIdle, CHECK_INTERVAL_MS);
      } else {
        // User logged out - stop tracking
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setShowWarning(false);
      }
    });

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      unsub();

      events.forEach((e) =>
        window.removeEventListener(e, throttledMarkActivity)
      );
      window.removeEventListener("storage", onStorage);

      try {
        channelRef.current?.close();
      } catch (e) {
        // Channel might already be closed
      }
    };
  }, [auth, onSignoutPath, markActivity, checkIdle, showWarning]);

  /* -------------------- RENDER -------------------- */
  return (
    <>
      {children}

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
              Session About to Expire
            </h2>

            <p className="text-gray-600 dark:text-gray-300 mb-4">
              You've been inactive for a while. For your security, you'll be logged out soon.
            </p>

            <div className="mb-6">
              <p className="text-4xl font-bold text-red-500 tabular-nums">
                {Math.floor(countdownSeconds / 60)}:{String(countdownSeconds % 60).padStart(2, '0')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                until automatic logout
              </p>
            </div>

            <button
              onClick={markActivity}
              className="w-full px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}

      {/* Session Expired Toast */}
      {showExpiredToast && (
        <div className="fixed bottom-6 right-6 z-[9999] bg-gray-900 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="font-semibold">Session Expired</p>
            <p className="text-sm text-gray-400">You've been logged out due to inactivity.</p>
          </div>
        </div>
      )}
    </>
  );
}
